import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LoadingSpinner from "../common/LoadingSpinner";
import apiClient from "../../services/apiClient";

const ManageAIInstructionsModal = ({ isOpen, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    templateName: "",
    instructions: ""
  });

  // Load templates when modal opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.getAIInstructionTemplates();
      if (response.success) {
        setTemplates(response.data.templates || []);
      }
    } catch (err) {
      console.error("Failed to load templates:", err);
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setFormData({
      templateName: template.template_name,
      instructions: template.instructions
    });
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleCreateNew = async () => {
    try {
      // Get system default instructions
      const response = await apiClient.getSystemDefaultInstructions();
      setFormData({
        templateName: "",
        instructions: response.data.instructions
      });
      setSelectedTemplate(null);
      setIsCreating(true);
      setIsEditing(false);
    } catch (err) {
      console.error("Failed to load system default:", err);
      setError("Failed to load default instructions");
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setIsCreating(false);
  };

  const handleCancelEdit = () => {
    if (selectedTemplate) {
      setFormData({
        templateName: selectedTemplate.template_name,
        instructions: selectedTemplate.instructions
      });
    }
    setIsEditing(false);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!formData.templateName.trim()) {
      setError("Template name is required");
      return;
    }

    if (!formData.instructions.trim()) {
      setError("Instructions are required");
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (isCreating) {
        // Create new template
        await apiClient.createAIInstructionTemplate(
          formData.templateName,
          formData.instructions
        );
      } else if (isEditing && selectedTemplate) {
        // Update existing template
        await apiClient.updateAIInstructionTemplate(
          selectedTemplate.template_id,
          formData.templateName,
          formData.instructions
        );
      }

      await loadTemplates();
      setIsEditing(false);
      setIsCreating(false);
      setSelectedTemplate(null);
      setFormData({ templateName: "", instructions: "" });
    } catch (err) {
      console.error("Failed to save template:", err);
      setError(err.response?.data?.message || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    try {
      setSaving(true);
      setError(null);
      await apiClient.deleteAIInstructionTemplate(templateToDelete.template_id);

      if (selectedTemplate?.template_id === templateToDelete.template_id) {
        setSelectedTemplate(null);
        setFormData({ templateName: "", instructions: "" });
      }

      await loadTemplates();
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    } catch (err) {
      console.error("Failed to delete template:", err);
      setError(err.response?.data?.message || "Failed to delete template");
      setShowDeleteConfirm(false);
      setTemplateToDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (template) => {
    try {
      setSaving(true);
      setError(null);
      await apiClient.setDefaultAIInstructionTemplate(template.template_id);
      await loadTemplates();
    } catch (err) {
      console.error("Failed to set default:", err);
      setError("Failed to set default template");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[85vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-slate-700 to-slate-800 text-white px-6 py-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold flex items-center">
                <FontAwesomeIcon icon="cog" className="mr-2" />
                Manage AI Instructions
              </h2>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <FontAwesomeIcon icon="times" size="lg" />
              </button>
            </div>
            <p className="text-sm text-slate-200 mt-2">
              Create and manage custom AI instruction templates for proposal generation
            </p>
          </div>

          {/* Body - Two Column Layout */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Panel - Template List */}
            <div className="w-80 border-r border-gray-200 bg-gray-50 overflow-y-auto">
              <div className="p-4">
                <button
                  onClick={handleCreateNew}
                  disabled={saving}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors flex items-center justify-center space-x-2 mb-4"
                >
                  <FontAwesomeIcon icon="plus" />
                  <span>Create New Template</span>
                </button>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="md" />
                  </div>
                ) : templates.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FontAwesomeIcon icon="file-alt" className="text-3xl mb-2" />
                    <p className="text-sm">No custom templates yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {templates.map((template) => (
                      <div
                        key={template.template_id}
                        onClick={() => handleSelectTemplate(template)}
                        className={`p-3 rounded-lg cursor-pointer transition-all ${
                          selectedTemplate?.template_id === template.template_id
                            ? "bg-blue-50 border-2 border-blue-500"
                            : "bg-white border border-gray-200 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-sm font-medium text-gray-900 truncate">
                                {template.template_name}
                              </h3>
                              {template.is_default === 1 && (
                                <span className="text-yellow-500">
                                  <FontAwesomeIcon icon="star" />
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(template.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Template Editor */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {error && (
                <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start">
                  <FontAwesomeIcon icon="exclamation-triangle" className="mr-2 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {isCreating || isEditing || selectedTemplate ? (
                <div className="flex-1 flex flex-col overflow-hidden p-6">
                  {/* Template Name */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={formData.templateName}
                      onChange={(e) => setFormData({ ...formData, templateName: e.target.value })}
                      disabled={!isEditing && !isCreating}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      placeholder="e.g., Detailed Technical Proposal"
                    />
                  </div>

                  {/* Instructions */}
                  <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Instructions *
                      </label>
                      <span className="text-xs text-gray-500">
                        {formData.instructions.length} characters
                      </span>
                    </div>
                    <textarea
                      value={formData.instructions}
                      onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                      disabled={!isEditing && !isCreating}
                      className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm disabled:bg-gray-100"
                      placeholder="Enter AI instructions..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="flex items-center space-x-2">
                      {selectedTemplate && !isEditing && !isCreating && (
                        <>
                          <button
                            onClick={handleEdit}
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
                          >
                            <FontAwesomeIcon icon="edit" className="mr-2" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleSetDefault(selectedTemplate)}
                            disabled={saving || selectedTemplate.is_default === 1}
                            className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:bg-gray-300 transition-colors"
                          >
                            <FontAwesomeIcon icon="star" className="mr-2" />
                            {selectedTemplate.is_default === 1 ? "Default" : "Set as Default"}
                          </button>
                          <button
                            onClick={() => handleDeleteClick(selectedTemplate)}
                            disabled={saving || selectedTemplate.is_default === 1}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 transition-colors"
                          >
                            <FontAwesomeIcon icon="trash" className="mr-2" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>

                    {(isEditing || isCreating) && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleCancelEdit}
                          disabled={saving}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSave}
                          disabled={saving || !formData.templateName.trim() || !formData.instructions.trim()}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors"
                        >
                          {saving ? (
                            <>
                              <FontAwesomeIcon icon="spinner" spin className="mr-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon="save" className="mr-2" />
                              {isCreating ? "Create Template" : "Save Changes"}
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <FontAwesomeIcon icon="arrow-left" className="text-4xl mb-4" />
                    <p>Select a template or create a new one</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 bg-gray-50">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && templateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                  <FontAwesomeIcon icon="exclamation-triangle" className="text-red-600 text-xl" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Delete Template
                </h3>
              </div>
              <p className="text-slate-600 mb-6">
                Are you sure you want to delete "{templateToDelete.template_name}"? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setTemplateToDelete(null);
                  }}
                  className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManageAIInstructionsModal;
